import { Prompt, useHistory } from 'react-router-dom';

import { useState, FunctionComponent, useEffect, useMemo, useRef } from 'react';

import { PaymentFields, PaymentFieldDefault } from '@/hooks/useFields/TransactionCreationFields/types';
import styles from './SinglePaymentForm.module.scss';
import sharedStyles from '@/styles/PaymentAndCollection/PaymentAndCollection.module.scss';
import {
  Box,
  Modal,
  Card,
  CollapsibleCard,
  toBool,
  withStyles,
  useForm,
  SupportedCurrencies,
  FundingSource,
  sanitizeToNumeric,
} from 'vopay';
import { Button, Title, InfoBanner } from '@/components/UI';
import { useScheduleExpiryMessage } from '@/hooks/useScheduleExpiryMessage';
const classes = withStyles({ ...styles, ...sharedStyles });
import {
  useContactAddressFields,
  useElinxBankAccountFields,
  useContactDetailsFields,
  useElinxDetailsFields,
  useInteracEtransferFields,
  useBankAccountFields,
  usePaymentSettingsFields,
  useTransactionDetailsFields,
  useAdditionalInformation,
  useElinxSettingsFields,
  useContactFields,
  useAmountFields,
  usePayFromFields,
  useElinxCardFields,
} from '@/hooks/useFields/TransactionCreationFields';
import { renderTransactionDetailsField } from '@/hooks/useFields/TransactionCreationFields/useTransactionDetailsFields';
import { isPaymentScheduled } from '@/hooks/useFields/TransactionCreationFields/useFundingOptionField';
import useInteracAutoDeposit from '@/hooks/useApi/useInteracAutoDeposit';
import useFetchClientAccountBalance from '@/hooks/useApi/useFetchClientAccountBalance';
import useFetchFundingOptions from '@/hooks/useApi/useFetchFundingOptions';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import ElinxImage from '../Images/elinx-preview.png';
import { useClientData } from '@/providers/ClientData.provider';
import ElinxSettingsDialogue from '@/components/Elinx/ElinxSettings/ElinxSettingsDialogue';

import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import useFetchElinxSettings from '@/hooks/useApi/useFetchElinxSettings';
import useGLCodes from '@/hooks/useApi/useGLCodes';
import useTransactionCodes from '@/hooks/useApi/useTransactionCodes';
import { UtilButton } from 'UI';
import useAccountBalance from '@/hooks/useApi/useAccountBalance';
import { useTranslation, Trans } from 'react-i18next';
import { useDataFetchIndicator } from '@/hooks';
import { BodyText, FieldData, InteracWordmark } from 'vopay';
import useContacts from '@/hooks/useApi/useContacts';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import usePortalDropdownContacts from '@/hooks/useApi/usePortalDropdownContacts';
import useFetchContactPaymentMethod from '@/hooks/useApi/useFetchContactPaymentMethod';
import MessageBox from '@/components/UI/MessageBox/MessageBox';
import GppGoodIcon from './svgs/gpp_good.svg';
import useFetchRailzBusinessAccount from '@/hooks/useApi/useFetchRailzBusinessAccount';
import { useDataDog } from '@/providers/Datadog.provider';
import usePreFundingSettings from '@/hooks/useApi/usePreFundingSettings';
import useFetchDefaultOperationalBankAccount from '@/hooks/useApi/useFetchDefaultOperationalBankAccount';
import FundingBanner from './FundingBanner/FundingBanner';
import { isSinglePaymentSelectionMethod } from './FundingBanner/singlePaymentSelectionMethod';

type SinglePaymentProps = {
  onSubmit: (values: PaymentFields) => void;
  defaultValues: PaymentFieldDefault;
  onAvailableBalanceChange: (balance: string) => void;
  onCurrencyChange?: (currency: SupportedCurrencies) => void;
};

const SinglePaymentForm: FunctionComponent<SinglePaymentProps> = ({
  defaultValues,
  onSubmit,
  onAvailableBalanceChange,
  onCurrencyChange,
}) => {
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'SinglePayment.Form' });
  const { fetchContact } = useContacts();
  const { fetchClientAccount } = useClientAccounts();
  const [autoDepositEnabled, setAutoDepositEnabled] = useState(false);
  const { paymentMethods, fetchPaymentMethods } = useFetchContactPaymentMethod();
  const { logMessage } = useDataDog();
  const [moreInfoNeeded, setMoreInfoNeeded] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const { GLCodes, fetchGLCodes, loading: loadingGLCodes } = useGLCodes();
  const [, dataFetchIndicator] = useDataFetchIndicator();
  const { transactionCodes, fetchTransactionCodes, loading: loadingTransactionCodes } = useTransactionCodes();
  const { dropdownContacts, getDropdownItems, fetchingContacts } = usePortalDropdownContacts({
    includeBalance: true,
  });
  const {
    response: { AccountType, clientAccountsEnabled, Country, AccountingSoftwareIntegrationEnabled, LoginUserName },
  } = useClientData();
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [elinxExpirationDays, setElinxExpirationDays] = useState<number>();
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { balance, fetchBalance } = useAccountBalance();
  const { fundingOptions, fetchFundingOptions, fetching: fundingOptionsFetching } = useFetchFundingOptions();
  const { fundingTrigger, fetchPreFundingSettings } = usePreFundingSettings();
  const { operationalBankAccount, fetchDefaultOperationalBankAccount } = useFetchDefaultOperationalBankAccount();
  const { fetchAutoDepositStatus, loading: loadingAutoDeposit } = useInteracAutoDeposit();
  const [, , , fetchRailzBusinessAccount] = useFetchRailzBusinessAccount({});
  const [accountingSoftwareHasAccounts, setAccountingSoftwareHasAccounts] = useState(false);

  const [clientAccountBalanceResponse, getClientAccountBalance] = useFetchClientAccountBalance({});
  const availableBalance = useMemo(() => {
    return clientAccountsEnabled ? clientAccountBalanceResponse.AvailableFunds : String(balance?.AvailableFunds || '');
  }, [clientAccountsEnabled, clientAccountBalanceResponse.AvailableFunds, String(balance?.AvailableFunds || '')]);

  useEffect(() => {
    dataFetchIndicator(loadingGLCodes);
  }, [loadingGLCodes]);
  useEffect(() => {
    dataFetchIndicator(loadingTransactionCodes);
  }, [loadingTransactionCodes]);
  useEffect(() => {
    dataFetchIndicator(loadingAutoDeposit);
  }, [loadingAutoDeposit]);

  useEffect(() => {
    const checkRailzAccounts = async () => {
      if (AccountingSoftwareIntegrationEnabled && LoginUserName) {
        try {
          const railzBusinessAccount = await fetchRailzBusinessAccount({
            PortalUser: LoginUserName,
          });

          if (!railzBusinessAccount.Success) {
            throw new Error(railzBusinessAccount.ErrorMessage);
          }

          const accountHasBeenSetUp = Object.values(railzBusinessAccount.Accounts).length > 0;
          setAccountingSoftwareHasAccounts(accountHasBeenSetUp);
        } catch (error) {
          // no error message display because it returns an error if railz isn't setup which in this case is the same as not having an accounting software connected
          console.error(error);
          setAccountingSoftwareHasAccounts(false);
        }
      } else {
        setAccountingSoftwareHasAccounts(false);
      }
    };

    checkRailzAccounts();
  }, []);

  useEffect(() => {
    if (clientAccountsEnabled) return;

    async function loadPreFundingSettings() {
      try {
        await fetchPreFundingSettings();
      } catch {
        toast.neutral(t('fetchPreFundingSettingsError', { defaultValue: 'Could not load your pre-funding settings' }));
      }
    }

    loadPreFundingSettings();
  }, [clientAccountsEnabled]);

  useEffect(() => {
    onAvailableBalanceChange(availableBalance || '');
  }, [availableBalance]);
  const [, , , fetchElinxSettings] = useFetchElinxSettings({});
  const lockedFeatures = useLockedFeatures();

  const { updateIsLocked } = useLockedFeatureDialogue();

  const hasBankAccount = useMemo(
    () => paymentMethods.some((pm) => pm.PaymentMethod === 'BankAccount'),
    [paymentMethods],
  );
  const [shouldShowAddressCard, setShouldShowAddressCard] = useState(false);
  const [isAddressRequired, setIsAddressRequired] = useState(false);
  const contactDetailsFields = useContactDetailsFields();
  const contactAddressFields = useContactAddressFields(Country, shouldShowAddressCard, isAddressRequired);
  const elinxDetailsFields = useElinxDetailsFields();
  const elinxBankAccountFields = useElinxBankAccountFields('Pay', Country);
  const transactionDetailsFields = useTransactionDetailsFields('Pay', fundingTrigger);
  const amountFields = useAmountFields(availableBalance || '', AccountType, 'Pay', fundingTrigger);
  const payFromFields = usePayFromFields(fundingOptions, fundingOptionsFetching, fundingTrigger);
  const contactFields = useContactFields(dropdownContacts, getDropdownItems, fetchingContacts);
  const additionalInformationFields = useAdditionalInformation(
    GLCodes,
    transactionCodes,
    accountingSoftwareHasAccounts,
  );
  const paymentSettingsFields = usePaymentSettingsFields('Pay', Country, paymentMethods);
  const interacEtransferFields = useInteracEtransferFields('Pay', autoDepositEnabled);
  const bankAccountFields = useBankAccountFields(Country, clientAccountsEnabled);
  const elinxSettingFields = useElinxSettingsFields('email', Country);
  const elinxCardFields = useElinxCardFields();

  const fieldDataList: FieldData<PaymentFieldDefault>[] = useMemo(() => {
    return [
      ...contactFields,
      ...contactDetailsFields,
      ...amountFields,
      ...payFromFields,
      ...paymentSettingsFields,
      ...bankAccountFields,
      ...interacEtransferFields,
      ...elinxBankAccountFields,
      ...elinxCardFields,
      ...transactionDetailsFields,
      ...additionalInformationFields,
      ...contactAddressFields,
      ...elinxDetailsFields,
      ...elinxSettingFields,
    ];
  }, [
    transactionDetailsFields,
    additionalInformationFields,
    contactDetailsFields,
    contactAddressFields,
    amountFields,
    payFromFields,
    elinxBankAccountFields,
    elinxCardFields,
    elinxDetailsFields,
    paymentSettingsFields,
    bankAccountFields,
    interacEtransferFields,
    contactFields,
    elinxSettingFields,
  ]);

  const toggleMoreInformation = () => {
    setMoreInfoNeeded(!moreInfoNeeded);
  };
  const { permissions } = usePermissions();

  useEffect(() => {
    fetchGLCodes();
    fetchTransactionCodes();
    getDropdownItems('');
  }, []);

  const { renderFormElements, ctx } = useForm<PaymentFieldDefault, PaymentFields>({
    config: fieldDataList,
    initialValues: defaultValues,
    onSubmit: (serializedData, formCtx) => {
      onSubmit({
        ...serializedData,
        PaymentSelectionMethod: formCtx.getSerializedValue('PaymentSelectionMethod') || '',
        InvoiceFileName: formCtx.getSerializedValue('InvoiceFileName') || '',
        RecipientName: formCtx.getSerializedValue('RecipientName') || '',
        ClientReferenceNumber: formCtx.getSerializedValue('ClientReferenceNumber'),
        Address1: formCtx.getSerializedValue('Address1'),
        City: formCtx.getSerializedValue('City'),
        Province: formCtx.getSerializedValue('Province'),
        Country: formCtx.getSerializedValue('Country') || Country,
        PostalCode: formCtx.getSerializedValue('PostalCode'),
        RecipientPhoneNumber: formCtx.getSerializedValue('RecipientPhoneNumber'),
        RecipientEmailAddress: formCtx.getSerializedValue('RecipientEmailAddress'),
        SendNotification: formCtx.getSerializedValue('SendNotification'),
        SendReminder: formCtx.getSerializedValue('SendReminder'),
        NotificationMethod: formCtx.getSerializedValue('NotificationMethod'),
        ReminderFrequency: formCtx.getSerializedValue('ReminderFrequency'),
        ProvideOrRequestPaymentInformation: formCtx.getSerializedValue('ProvideOrRequestPaymentInformation'),
        AccountNumber: formCtx.getSerializedValue('AccountNumber'),
        ABARoutingNumber: formCtx.getSerializedValue('ABARoutingNumber'),
        FinancialInstitutionNumber: formCtx.getSerializedValue('FinancialInstitutionNumber'),
        BranchTransitNumber: formCtx.getSerializedValue('BranchTransitNumber'),
        FirstName: formCtx.getSerializedValue('FirstName'),
        LastName: formCtx.getSerializedValue('LastName'),
        CompanyName: formCtx.getSerializedValue('CompanyName'),
      });
    },
    isReviewing: isUnderReview,
  });

  // Only the "request" flow can expire (it's an eLinx link waiting to be accepted) — a direct
  // scheduled payment has no expiry concept.
  const paymentDate =
    ctx.values.ProvideOrRequestPaymentInformation === 'request' &&
    ctx.values.ScheduleType === 'recurring' &&
    ctx.values.StartDate
      ? new Date(Number(ctx.values.StartDate))
      : undefined;
  const scheduleExpiryMessage = useScheduleExpiryMessage(paymentDate, elinxExpirationDays);

  const isNewContact = useMemo(() => ctx.values.Contact === 'new', [ctx.values.Contact]);
  const isNewPaymentMethod = useMemo(
    () => ctx.values.PaymentMethod === 'new' || ctx.values.PaymentMethod === 'request',
    [ctx.values.PaymentMethod],
  );
  const currency = useMemo(() => {
    return ctx.values.Currency !== '' ? ctx.values.Currency || 'CAD' : 'CAD';
  }, [ctx.values.Currency]);
  const isScheduled = useMemo(
    () => isPaymentScheduled(ctx.values),
    [ctx.values.ScheduleType, ctx.values.TakeFromAccountOn],
  );

  useEffect(() => {
    if (currency && onCurrencyChange) {
      //TODO - need to switch type to SupportedCurrencies throughout the form.
      const currencyMap = new Map<string, SupportedCurrencies>([
        ['CAD', 'CAD'],
        ['USD', 'USD'],
      ]);

      const mappedCurrency = currencyMap.get(currency) ?? 'CAD';
      onCurrencyChange(mappedCurrency);
    }
  }, [currency]);
  useEffect(() => {
    const sendRequest = () => {
      if (ctx.values.Contact && clientAccountsEnabled) {
        getClientAccountBalance({
          ClientAccountID: ctx.values.Contact,
          Currency: currency,
        });
      }
    };
    const timeoutRef = setTimeout(sendRequest, 100);
    return () => {
      clearTimeout(timeoutRef);
    };
  }, [ctx.values.Contact, currency]);

  useEffect(() => {
    if (clientAccountsEnabled) return;

    // Currency changed, so any previously selected PayFrom may no longer be valid. Reset to the
    // placeholder under `never` (auto-fill re-suggests once amount is known); other triggers stay
    // pinned to `balance` since the field is hidden and must not trigger a manual funding.
    ctx.setFieldValue('PayFrom', fundingTrigger === 'never' ? '' : 'balance');
    ctx.setFieldTouched('PayFrom', false);

    const loadBalanceAndFundingOptions = async () => {
      try {
        await Promise.all([
          fetchBalance(ctx.values.Currency || 'CAD'),
          fetchFundingOptions({ Currency: ctx.values.Currency || 'CAD' }),
          fetchDefaultOperationalBankAccount({ Currency: ctx.values.Currency || 'CAD' }),
        ]);
      } catch (error) {
        logMessage('Error loading balance/funding options', 'error', {
          feature: 'Single Payment',
          errorMessage: error instanceof Error ? error.message : String(error),
          trace: error instanceof Error ? error.stack : undefined,
        });
        toast.neutral(error instanceof Error ? error.message : String(error));
      }
    };

    loadBalanceAndFundingOptions();
  }, [ctx.values.Currency, fundingTrigger]);

  // Placeholder-first Pay From: suggests balance/bank once amount is entered, keeps a manual
  // selection unless the covered/short outcome flips.
  const prevPayFromOutcomeRef = useRef<'covered' | 'short' | undefined>(undefined);
  // Remembers a manual Pay From choice across a trip through Scheduled — otherwise switching
  // to Scheduled and back would silently re-run the auto-suggestion and lose it.
  const payFromBeforeScheduleRef = useRef<FundingSource | ''>('');
  useEffect(() => {
    if (fundingTrigger !== 'never') return;

    // Scheduled payments hide this field and never create a funding transaction — keep it pinned
    // to `balance` so no stale suggestion leaks through.
    if (isScheduled) {
      prevPayFromOutcomeRef.current = undefined;
      if (ctx.values.PayFrom !== 'balance') {
        payFromBeforeScheduleRef.current = (ctx.values.PayFrom as FundingSource) || '';
        ctx.setFieldValue('PayFrom', 'balance');
        ctx.setFieldTouched('PayFrom', false);
      }
      return;
    }

    if (payFromBeforeScheduleRef.current) {
      ctx.setFieldValue('PayFrom', payFromBeforeScheduleRef.current);
      ctx.setFieldTouched('PayFrom', true);
      payFromBeforeScheduleRef.current = '';
      return;
    }

    // No real choice to make (no bank/credit for this currency) — the field is hidden, so force
    // 'balance' rather than ever suggesting a source that isn't actually an available option.
    if (fundingOptions.length <= 1) {
      prevPayFromOutcomeRef.current = undefined;
      if (ctx.values.PayFrom !== 'balance') ctx.setFieldValue('PayFrom', 'balance');
      return;
    }

    if (!ctx.values.Amount) {
      prevPayFromOutcomeRef.current = undefined;
      if (!ctx.touched.PayFrom) {
        ctx.setFieldValue('PayFrom', '');
      }
      return;
    }

    const outcome: 'covered' | 'short' =
      Number(sanitizeToNumeric(ctx.values.Amount)) <= Number(availableBalance || 0) ? 'covered' : 'short';
    const outcomeChanged = prevPayFromOutcomeRef.current !== undefined && prevPayFromOutcomeRef.current !== outcome;
    prevPayFromOutcomeRef.current = outcome;

    if (!ctx.touched.PayFrom || outcomeChanged) {
      const suggestion: FundingSource = outcome === 'covered' ? 'balance' : 'bank';
      ctx.setFieldValue('PayFrom', suggestion);
      ctx.setFieldTouched('PayFrom', false);
    }
  }, [ctx.values.Amount, availableBalance, fundingTrigger, isScheduled, fundingOptions]);

  useEffect(() => {
    if (ctx.values.Contact && ctx.values.Contact !== 'new') {
      fetchPaymentMethods(ctx.values.Contact);
    }
  }, [ctx.values.Contact]);

  useEffect(() => {
    if (!hasBankAccount && ctx.values.Contact && ctx.values.Contact !== 'new') {
      ctx.setFieldTouched('Contact', true);
    }
  }, [defaultValues, hasBankAccount]);

  useEffect(() => {
    if (clientAccountsEnabled) {
      if (ctx.values.PaymentMethod === 'Bank Account' && hasBankAccount) {
        ctx.setValues({ ...ctx.values, ProvideOrRequestPaymentInformation: 'provide' });
      }
    }
  }, [ctx.values.ClientAccount, ctx.values.ProvideOrRequestPaymentInformation, hasBankAccount]);

  const getElinxSettings = async () => {
    try {
      const res = await fetchElinxSettings({});
      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }
      setIsVisible(false);
      ctx.setValues({
        ...ctx.values,
        SendNotification: toBool(res.ELinxSettings.ReceiverNotificationsEnabled),
        SendReminder: toBool(res.ELinxSettings.ELinxReminderEnabled),
        ReminderFrequency: res.ELinxSettings.ELinxReminderFrequency,
        NotificationMethod: res.ELinxSettings.DefaultNotificationMethod,
      });
    } catch (err) {
      console.error(err);
      alertDialog.caution('Error', t('failedFetchElinx'), {
        abortLabel: t('dismiss'),
      });
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const debounceTimeout = setTimeout(async () => {
      if (
        ctx.values.Contact !== '' &&
        ctx.values.ProvideOrRequestPaymentInformation !== 'request' &&
        ctx.values.PaymentSelectionMethod === 'email' &&
        ctx.values.EtransferConnectionType === 'email' &&
        !ctx.errors.RecipientEmailAddress
      ) {
        try {
          const resp = await fetchAutoDepositStatus(ctx.values.RecipientEmailAddress);
          if (!signal.aborted) {
            setAutoDepositEnabled(resp.AutoDepositEnabled);
          }
        } catch {
          setAutoDepositEnabled(false);
        }
      }
    }, 500);
    if (ctx.values.EtransferConnectionType === 'phone') {
      setAutoDepositEnabled(false);
    }
    return () => {
      clearTimeout(debounceTimeout);
      controller.abort();
    };
  }, [
    ctx.values.RecipientEmailAddress,
    ctx.values.Contact,
    ctx.values.PaymentMethod,
    ctx.values.EtransferConnectionType,
    ctx.values.ProvideOrRequestPaymentInformation,
    ctx.values.PaymentSelectionMethod,
    ctx.errors.RecipientEmailAddress,
  ]);

  // for the schedule expiry banner
  useEffect(() => {
    const fetchExpirationDays = async () => {
      try {
        const res = await fetchElinxSettings({});
        if (!res.Success) {
          throw new Error(res.ErrorMessage);
        }
        setElinxExpirationDays(Number(res.ELinxSettings.ELinxExpirationTime));
      } catch (err) {
        console.error(err);
      }
    };
    fetchExpirationDays();
  }, []);

  // Split so the expiry banner can render right after Recurrence, before Description/Message.
  const scheduleBreakIndex = transactionDetailsFields.findIndex((field) => field.fieldName === 'Recurrence');
  const scheduleFieldsRows = transactionDetailsFields.slice(0, scheduleBreakIndex + 1);
  const remainingTransactionDetailsRows = transactionDetailsFields.slice(scheduleBreakIndex + 1);

  const scheduleFieldsElements = useMemo(() => {
    return renderFormElements({ config: scheduleFieldsRows, override: renderTransactionDetailsField });
  }, [renderFormElements, scheduleFieldsRows]);

  const remainingTransactionDetailsElements = useMemo(() => {
    return renderFormElements({ config: remainingTransactionDetailsRows, override: renderTransactionDetailsField });
  }, [renderFormElements, remainingTransactionDetailsRows]);

  const additionalInformationElements = useMemo(() => {
    return renderFormElements({ config: additionalInformationFields });
  }, [renderFormElements, additionalInformationFields]);

  const contactDetailsElements = useMemo(() => {
    return renderFormElements({ config: contactDetailsFields });
  }, [renderFormElements, contactDetailsFields]);

  const contactAddressElements = useMemo(() => {
    return renderFormElements({ config: contactAddressFields });
  }, [renderFormElements, contactAddressFields]);

  const paymentSettingsElements = useMemo(() => {
    return renderFormElements({ config: paymentSettingsFields });
  }, [renderFormElements, paymentSettingsFields]);

  const bankAccountElements = useMemo(() => {
    return renderFormElements({ config: bankAccountFields });
  }, [renderFormElements, bankAccountFields]);

  const interacEtransferElements = useMemo(() => {
    return renderFormElements({ config: interacEtransferFields });
  }, [renderFormElements, interacEtransferFields]);

  const elinxBankAccountElements = useMemo(() => {
    return renderFormElements({ config: elinxBankAccountFields });
  }, [renderFormElements, elinxBankAccountFields]);

  const elinxDetailsElements = useMemo(() => {
    return renderFormElements({ config: elinxDetailsFields });
  }, [renderFormElements, elinxDetailsFields]);

  const elinxCardElements = useMemo(() => {
    return renderFormElements({ config: elinxCardFields });
  }, [renderFormElements, elinxCardFields]);

  const elinxSettingElements = useMemo(() => {
    return renderFormElements({
      config: elinxSettingFields,
    });
  }, [renderFormElements, elinxSettingFields]);

  const amountElements = useMemo(() => {
    return renderFormElements({ config: amountFields });
  }, [renderFormElements, amountFields]);

  const payFromElements = useMemo(() => {
    return renderFormElements({ config: payFromFields });
  }, [renderFormElements, payFromFields]);

  const contactElements = useMemo(() => {
    return renderFormElements({ config: contactFields });
  }, [renderFormElements, contactFields]);

  useEffect(() => {
    setIsAddressRequired(
      !!(
        ctx.values.PaymentSelectionMethod?.includes('bank') &&
        ctx.values.ProvideOrRequestPaymentInformation === 'provide'
      ),
    );
  }, [ctx.values.PaymentSelectionMethod, ctx.values.ProvideOrRequestPaymentInformation]);

  useEffect(() => {
    const checkAddressFields = async () => {
      if (ctx.values.Contact === 'new') {
        setShouldShowAddressCard(true);
        return;
      }

      if (!isAddressRequired) {
        setShouldShowAddressCard(false);
        return;
      }

      if (!ctx.values.Contact) {
        setShouldShowAddressCard(false);
        return;
      }

      try {
        let address1 = '';
        let city = '';
        let province = '';
        let country = '';
        let postalCode = '';
        let contactPhone = '';

        if (clientAccountsEnabled) {
          if (!permissions.ViewClientAccounts) {
            setShouldShowAddressCard(true);
            return;
          }
          const account = await fetchClientAccount(ctx.values.Contact);

          if (account) {
            address1 = account.Address1 || '';
            city = account.City || '';
            province = account.Province || '';
            country = account.Country || '';
            postalCode = account.PostalCode || '';
            contactPhone = account.PhoneNumber || '';
          }
        } else {
          const contact = await fetchContact(ctx.values.Contact);
          if (contact) {
            address1 = contact.Address1 || '';
            city = contact.City || '';
            province = contact.Province || '';
            country = contact.Country || '';
            postalCode = contact.PostalCode || '';
            contactPhone = contact.PhoneNumber || '';
          }
        }

        const bankPhone = ctx.values.BankPhoneNumber || '';

        const hasAddress1 = address1.trim() !== '';
        const hasCity = city.trim() !== '';
        const hasProvince = province.trim() !== '';
        const hasCountry = country.trim() !== '';
        const hasPostalCode = postalCode.trim() !== '';

        // Helper function to check if phone is valid (not empty and not default '0000000000')
        const isValidPhone = (phone: string) => phone.trim() !== '' && phone !== '0000000000';

        // Check if phone number is needed (for US)
        let phoneNeeded = Country === 'US' && isAddressRequired;
        let hasValidPhone = false;

        if (phoneNeeded) {
          // Determine which phone to use: contact phone first, then bank phone
          let phoneToUse = '';
          if (isValidPhone(contactPhone)) {
            phoneToUse = contactPhone;
          } else if (isValidPhone(bankPhone)) {
            phoneToUse = bankPhone;
          }

          hasValidPhone = phoneToUse !== '';

          // Set RecipientPhoneNumber if we have a phone
          if (hasValidPhone && !ctx.values.RecipientPhoneNumber) {
            ctx.setFieldValue('RecipientPhoneNumber', phoneToUse);
          }
        }

        const addressComplete = hasAddress1 && hasCity && hasProvince && hasCountry && hasPostalCode;
        const allFieldsComplete = phoneNeeded ? addressComplete && hasValidPhone : addressComplete;

        setShouldShowAddressCard(!allFieldsComplete);
      } catch (err) {
        console.error('Error fetching address:', err);
        setShouldShowAddressCard(true);
      }
    };

    checkAddressFields();
  }, [ctx.values.Contact, isAddressRequired, clientAccountsEnabled, Country, ctx.values.BankPhoneNumber]);
  return (
    <div>
      <form
        onSubmit={(e) => {
          if (!isUnderReview) {
            setIsUnderReview(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            ctx.handleSubmit(e);
          }
          e.preventDefault();
        }}
        className={classes('form')}
      >
        <div className={classes('formContainer')}>
          <Box grow="1" flx="col" className={classes('mainContent')}>
            <Box flx="col" gapSize="24px">
              <Card className={classes('card')}>
                <Box flx="col" gapSize="32px" height="auto">
                  <div className={classes('grid')}>
                    {contactElements}
                    {isNewContact && contactDetailsElements}
                  </div>

                  <div className={classes('grid')}>
                    <div className={classes('currencyFeild')}>{amountElements}</div>
                  </div>

                  <div className={classes('grid')}>{payFromElements}</div>

                  <div className={classes('grid')}>{paymentSettingsElements}</div>
                  {(isNewContact || isNewPaymentMethod || ctx.values.PaymentSelectionMethod === 'email') && (
                    <>
                      {ctx.values.PaymentSelectionMethod?.includes('bank') &&
                        ctx.values.ProvideOrRequestPaymentInformation === 'request' && (
                          <Card className={classes('subCard')} hoverEffect={false} title={t('bankAccountTitle')}>
                            <div className={classes('grid')}>{elinxBankAccountElements}</div>
                          </Card>
                        )}
                      {ctx.values.PaymentSelectionMethod?.includes('bank') &&
                        ctx.values.ProvideOrRequestPaymentInformation === 'provide' && (
                          <Card className={classes('subCard')} hoverEffect={false} title={t('bankAccountTitle')}>
                            <div className={classes('grid')}>{bankAccountElements}</div>
                          </Card>
                        )}
                      {ctx.values.PaymentSelectionMethod?.includes('email') &&
                        ctx.values.ProvideOrRequestPaymentInformation === 'provide' && (
                          <Card
                            className={classes('subCard')}
                            hoverEffect={false}
                            title={
                              <Trans i18nKey="interacTitle" t={t}>
                                <InteracWordmark />
                              </Trans>
                            }
                          >
                            <Box flx="col" gap="md" base="8">
                              <div className={classes('grid')}>{interacEtransferElements}</div>
                              {autoDepositEnabled && (
                                <MessageBox>
                                  <Box flx="row" ai="center" gapSize="16px">
                                    <img src={GppGoodIcon} alt="good icon" />
                                    <div>
                                      <Trans
                                        i18nKey="autoDepositMessage"
                                        t={t}
                                        components={{ bold: <span style={{ fontWeight: 600 }} /> }}
                                      />
                                    </div>
                                  </Box>
                                </MessageBox>
                              )}
                            </Box>
                          </Card>
                        )}
                      {(ctx.values.PaymentSelectionMethod?.includes('credit') ||
                        ctx.values.PaymentSelectionMethod?.includes('debitcard')) &&
                        ctx.values.ProvideOrRequestPaymentInformation === 'request' && (
                          <Card
                            className={classes('subCard')}
                            hoverEffect={false}
                            title={t('cardSettingsTitle', { defaultValue: 'Card Settings' })}
                          >
                            <div className={classes('grid')}>{elinxCardElements}</div>
                          </Card>
                        )}
                    </>
                  )}
                  <div className={classes('grid')}>{scheduleFieldsElements}</div>
                  {scheduleExpiryMessage && <InfoBanner>{scheduleExpiryMessage}</InfoBanner>}
                  <FundingBanner
                    fundingTrigger={fundingTrigger}
                    isScheduled={isScheduled}
                    fundingOptions={fundingOptions}
                    payFrom={ctx.values.PayFrom || ''}
                    amount={ctx.values.Amount || ''}
                    currency={ctx.values.Currency || 'CAD'}
                    operationalBankAccount={operationalBankAccount}
                    paymentSelectionMethod={
                      isSinglePaymentSelectionMethod(ctx.values.PaymentSelectionMethod)
                        ? ctx.values.PaymentSelectionMethod
                        : undefined
                    }
                    bankingPaymentRail={ctx.values.BankingPaymentRail}
                  />
                  <div className={classes('grid')}>{remainingTransactionDetailsElements}</div>
                </Box>
                {!moreInfoNeeded && !isUnderReview && (
                  <UtilButton
                    size="large"
                    iconLeft="add"
                    onClick={() => toggleMoreInformation()}
                    disabled={isUnderReview}
                    className={classes('showMoreButton')}
                  >
                    {t('addMoreInformationBtn')}
                  </UtilButton>
                )}
                {(moreInfoNeeded || isUnderReview) && (
                  <>
                    <UtilButton
                      size="large"
                      iconLeft="remove"
                      onClick={() => toggleMoreInformation()}
                      disabled={isUnderReview}
                      className={classes('showMoreButton')}
                    >
                      {t('hideAdditionalInformationBtn')}
                    </UtilButton>

                    <div className={classes('grid', 'additionalInformationContainer')}>
                      {additionalInformationElements}
                    </div>
                  </>
                )}
              </Card>

              {shouldShowAddressCard && !isAddressRequired && (
                <CollapsibleCard className={classes('collapsibleCard')}>
                  <CollapsibleCard.Trigger indicator="chevron" style={{ '--collapsibleCardPadding': '24px' }}>
                    <Box gap="sm" ai="center">
                      <span>{Country === 'US' ? t('phoneAndAddressTitle') : t('addressTitle')}</span>
                    </Box>
                  </CollapsibleCard.Trigger>

                  <CollapsibleCard.Content style={{ '--collapsibleCardPadding': '24px' }}>
                    <div className={classes('grid')}>{contactAddressElements}</div>
                  </CollapsibleCard.Content>
                </CollapsibleCard>
              )}
              {shouldShowAddressCard && isAddressRequired && (
                <Card title={Country === 'US' ? t('phoneAndAddressTitle') : t('addressTitle')}>
                  <div className={classes('grid')}>{contactAddressElements}</div>
                </Card>
              )}
              {ctx.values.ProvideOrRequestPaymentInformation === 'request' && (
                <Card title={t('eLinxDetailsTitle')}>
                  <div className={classes('gridElinxDetails')}>{elinxDetailsElements}</div>
                </Card>
              )}
              {ctx.values.ProvideOrRequestPaymentInformation === 'request' && (
                <Card title={t('notificationSettingsTitle')}>
                  <div className={classes('grid')}>{elinxSettingElements}</div>
                </Card>
              )}
            </Box>
            <Box jc="sb" ai="fe" pt="md">
              <Box jc="fs">
                {ctx.values.ProvideOrRequestPaymentInformation === 'request' && (
                  <Button
                    colorScheme="secondary"
                    type="submit"
                    onClick={() => {
                      !lockedFeatures.PayLinkEnabled || !permissions.ViewElinxSettings
                        ? updateIsLocked(true, 'Elinx')
                        : setIsVisible(true);
                    }}
                  >
                    {t('editElinxSettingsBtn')}
                  </Button>
                )}
              </Box>
              <Box flx="row" gap="common" ai="fe">
                {ctx.values.ProvideOrRequestPaymentInformation === 'request' && (
                  <Box jc="fe">
                    <Button
                      id="See-Preview"
                      colorScheme="secondary"
                      type="button"
                      onClick={() => setPreviewVisible(true)}
                    >
                      {t('previewElinxBtn')}
                    </Button>
                  </Box>
                )}

                <Box jc="fe" gap="sm">
                  {isUnderReview ? (
                    <Button id="Fund-back" colorScheme="secondary" onClick={() => setIsUnderReview(false)}>
                      {t('backBtn')}
                    </Button>
                  ) : null}
                  <div
                    onClick={() => {
                      if (!ctx.isValid) {
                        logMessage('Disabled submit clicked', 'warn', {
                          feature: 'Single Payment',
                          errors: ctx.errors,
                          values: ctx.values,
                        });
                      }
                    }}
                  >
                    <Button id="Fund-submit" disabled={!ctx.isValid} colorScheme="primary" type="submit">
                      {isUnderReview ? t('submitBtn') : t('reviewBtn')}
                    </Button>
                  </div>
                </Box>
              </Box>
            </Box>
          </Box>
        </div>
        <Prompt when={ctx.hasPendingData} message={t('pendingDataMessage')} />

        <Modal isVisible={previewVisible} enableBgClick={false}>
          <div className={classes('container')}>
            <Box flx="col" ai="center">
              <div className={classes('titleContainer')}>
                <Title>{t('ElinxPreview.title')}</Title>
              </div>
              <div className={classes('descriptionContainer')}>
                <BodyText>{t('ElinxPreview.description1')}</BodyText>
                <BodyText>{t('ElinxPreview.description2')}.</BodyText>
              </div>
              <div>
                <img src={ElinxImage} alt="elinx-preview" />
              </div>
              <div className={classes('buttonBar')}>
                <Button
                  id="ManageBranding-Settings"
                  colorScheme="secondary"
                  type="button"
                  onClick={() => {
                    history.push('/settings/brand-settings');
                  }}
                >
                  {t('ElinxPreview.manageBtn')}
                </Button>
                <Button
                  id="AddLabel-Accept"
                  colorScheme="primary"
                  type="button"
                  onClick={() => {
                    setPreviewVisible(false);
                  }}
                >
                  {t('ElinxPreview.closeBtn')}
                </Button>
              </div>
            </Box>
          </div>
        </Modal>
      </form>
      <ElinxSettingsDialogue
        isVisible={isVisible}
        onCloseModal={() => setIsVisible(false)}
        onFetchElinxSettings={getElinxSettings}
      />
    </div>
  );
};

export default SinglePaymentForm;
